import { AggregationType, VISUALIZATIONS_MAP } from "~/ui-rtk/constants/charts"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import { useLastUpdateDate } from "~/ui-rtk/hooks/cube"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"
import useMobileView from "~/ui-rtk/hooks/mobile-view"
import useAggregationGroups from "~/ui-rtk/hooks/aggregation-groups"
import BrandValueOverviewChart, {
  WIDGET as chartWidgetId,
} from "./components/Chart/BrandValueOverviewChart"
import BrandValueOverviewTable, {
  TABLE_WIDGETS,
} from "./components/Table/BrandValueOverviewTable"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { useMemo } from "react"
import { Card, RichTooltip } from "~/ui-rtk/components/ui/common"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import MultiSelectWithChips from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import BrandValueByChannelGroupChart from "./components/BrandValueByChannelGroupChart/BrandValueByChannelGroupChart"
import BrandValueByPayoutCurve from "./components/BrandValueByPayoutCurve/BrandValueByPayoutCurve"
import { useUrlParamsStateArray } from "~/ui-rtk/hooks/urlParamsState"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import { getLightColor, stringToColor } from "~/ui-rtk/utils/chart-utils"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import {
  getMetricsChartPresets,
  TAllMetricsChartsKey,
} from "./constants/all-metrics-chart-presets"
import AnimatedContainer from "~/ui-rtk/components/ui/controls/AnimatedButton/AnimatedButton"
import { useGaMultiPropertyEnabled } from "~/ui-rtk/hooks/ga-multi-property-access"
import {
  metricEntryFor,
  useMetricDictionary,
} from "~/ui-rtk/hooks/metric-dictionary"

const dayjs = useDate()

export interface TBrandsValueOverviewDashboardProps {
  title: string
  slug: string
  periodPicker?: { visible: boolean }
  parent: {
    slug: string
    title: string
  }
  dateRangeRestrictions?: Partial<{ minDate: Date; maxDate: Date }>
  cubeQueryParams?: Record<string, string | string[]>
  props?: Record<string, unknown>
  toggleAmazon?: () => void
  amazonEnabled?: boolean
}

const TABLES = {
  CHANNEL_GROUP: "channel-group",
  PLATFORM: "platform",
  CHANNEL: "channel",
}

const lastDateChipDateMetric =
  "company_last_updated_date.brand_value_overview_max_date"

export const WIDGET_IDS = [chartWidgetId, ...Object.values(TABLE_WIDGETS)]

const tables = {
  [TABLES.CHANNEL]: {
    id: TABLES.CHANNEL,
    label: "Channel",
    widget: TABLE_WIDGETS.CHANNEL,
  },
  [TABLES.CHANNEL_GROUP]: {
    id: TABLES.CHANNEL_GROUP,
    label: "Channel Group",
    widget: TABLE_WIDGETS.CHANNEL_GROUP,
  },
  [TABLES.PLATFORM]: {
    id: TABLES.PLATFORM,
    label: "Platform",
    widget: TABLE_WIDGETS.PLATFORM,
  },
}

const TAB_URL_PARAM = "tab"
const CHART_SELECTED_METRICS_PARAM = "csm"

export default function BrandsValueOverviewDashboard({
  title,
  slug,
  parent,
  dateRangeRestrictions,
  cubeQueryParams = {},
  // toggleAmazon,
  // amazonEnabled,
  props: contentProps = {},
}: TBrandsValueOverviewDashboardProps) {
  const isMobile = useMobileView()
  const { params, setParams } = useURLSearchParams()

  const visualization = VISUALIZATIONS_MAP[chartWidgetId]
  const { graphs = [], groups = [] } = visualization?.props ?? {}
  const { depVar, amazonEnabled, toggleAmazon, depVarScaler } = useDepVar()
  const isGaMultiPropertyEnabled = useGaMultiPropertyEnabled()
  // Metric titles come from the shared Metric Dictionary (single source of
  // truth); the graph's own `label` is the fallback for anything not catalogued.
  const { metricMap } = useMetricDictionary()

  const allowedGraphs = useMemo(
    () =>
      graphs.filter(
        ({
          isEnabled,
        }: {
          isEnabled?: ({
            depVar,
            amazonEnabled,
            isGaMultiPropertyEnabled,
          }: {
            depVar: string
            amazonEnabled: boolean
            isGaMultiPropertyEnabled?: boolean
          }) => boolean
        }) =>
          !isEnabled ||
          isEnabled({ depVar, amazonEnabled, isGaMultiPropertyEnabled }),
      ),
    [graphs, depVar, amazonEnabled, isGaMultiPropertyEnabled],
  )

  const allowedGraphsDataKeys = useMemo(
    () => allowedGraphs.map(({ dataKey }: { dataKey: string }) => dataKey),
    [allowedGraphs],
  )

  const graphsWithColors = useMemo(
    () =>
      allowedGraphs.map(
        (graph: { dataKey: string; label: string }, idx: number) => {
          const dictEntry = metricEntryFor(metricMap, graph.dataKey)
          return {
            ...graph,
            label: dictEntry?.title ?? graph.label,
            // Keep the original label in the color seed so series colors stay
            // stable regardless of dictionary title changes.
            color: getLightColor(
              stringToColor(`${graph.dataKey}${idx}${graph.label}`),
            ),
          }
        },
      ),
    [allowedGraphs, metricMap],
  )

  const optionsWithGroups = useMemo(
    () =>
      graphsWithColors.map(
        ({
          label,
          group,
          dataKey,
          color,
          isBeta,
        }: {
          label: string
          group: string
          dataKey: string
          color: string
          isBeta: boolean
        }) => ({
          label,
          group,
          value: dataKey,
          color,
          isBeta,
        }),
      ),
    [graphsWithColors],
  )

  const { state: selectedMetricsKeys, setState: setSelectedMetricsKeys } =
    useUrlParamsStateArray<Array<string>>(
      CHART_SELECTED_METRICS_PARAM,
      getMetricsChartPresets(amazonEnabled, isGaMultiPropertyEnabled)
        .BASELINE_REVENUE_BRAND_VALUE.metrics,
    )

  const selectedMetrics = useMemo(
    () =>
      optionsWithGroups.filter(({ value }: { value: string }) =>
        (selectedMetricsKeys as string[]).includes(value),
      ),
    [optionsWithGroups, selectedMetricsKeys],
  )

  const { lastDate: lastDate, isLoading: isLastUpdateDateLoading } =
    useLastUpdateDate({
      dateMetric: lastDateChipDateMetric,
    })
  const activeTable = useMemo(
    () => tables[params[TAB_URL_PARAM] ?? TABLES.CHANNEL],
    [params],
  )

  const { dateRange, compareRange } = useMinMaxDateRange(
    dateRangeRestrictions?.minDate,
    dateRangeRestrictions?.maxDate ??
      (lastDate ? new Date(lastDate) : undefined),
  )
  const { granularity, setPeriod, setComparePeriod, setGranularity } =
    usePeriod()

  const { commonGranularityPicker } = contentProps as {
    commonGranularityPicker?: boolean
  }

  const aggregationGroups = useAggregationGroups()

  const handleSetWidget = (tab: string) => {
    setParams({
      [TAB_URL_PARAM]: tab.toLowerCase(),
    })
  }

  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const handleSetDataAggType = (aggType: AggregationType) => {
    setGranularity(aggType)
  }

  const presetsDropdownMenu = useMemo(
    () =>
      Object.values(
        getMetricsChartPresets(amazonEnabled, isGaMultiPropertyEnabled),
      ).map(({ label, value }) => ({
        label,
        value,
      })),
    [amazonEnabled, isGaMultiPropertyEnabled],
  )

  const handleSetPreset = (presetId: string | number) => {
    const { metrics } = getMetricsChartPresets(
      amazonEnabled,
      isGaMultiPropertyEnabled,
    )[presetId as TAllMetricsChartsKey]
    const readyMetrics = metrics.filter(metric =>
      allowedGraphsDataKeys.includes(metric),
    )
    setSelectedMetricsKeys(readyMetrics)
  }

  // Merge dep_var configuration with cubeQueryParams to ensure charts refresh when Amazon toggle changes
  const enrichedCubeQueryParams = useMemo(
    () => ({
      ...cubeQueryParams,
      depVar,
      depVarScaler,
    }),
    [cubeQueryParams, depVar, depVarScaler],
  )

  return (
    <div>
      <div
        className={cn({
          "flex items-start justify-between flex-col ": !isMobile,
        })}
      >
        <div className="text-start w-full">
          <Breadcrumbs
            items={[
              {
                slug: parent.slug,
                name: parent.title,
              },
              {
                slug,
                name: title,
              },
            ]}
          />
        </div>
        <div className="flex justify-between items-start gap-system-sm w-full">
          <div className="h-full flex-shrink-0">
            <DropdownMenu
              className="relative inline-block p-0"
              options={presetsDropdownMenu}
              onItemClick={handleSetPreset}
            >
              <AnimatedContainer>
                <span className="flex items-center gap-system-xs">
                  Select Preset
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-system-md w-system-md"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              </AnimatedContainer>
            </DropdownMenu>
          </div>
          <div className="w-full">
            <MultiSelectWithChips
              options={optionsWithGroups}
              groups={groups}
              showTypeAhead
              value={selectedMetrics}
              onChange={(options: TMultiSelectOption[]) =>
                setSelectedMetricsKeys(options.map(({ value }) => value))
              }
            />
          </div>
          <div className="flex gap-system-xs justify-end items-stretch">
            {toggleAmazon && (
              <AmazonToggle
                amazonEnabled={amazonEnabled}
                toggleAmazon={toggleAmazon}
              />
            )}
            {commonGranularityPicker && (
              <div className="join">
                {aggregationGroups.length > 1
                  ? aggregationGroups.map(({ type, long }) => (
                      <Button
                        variant={{ variant: "solid", color: "blue" }}
                        key={type}
                        title={long}
                        className={cn(
                          `join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md`,
                          { "bg-system-blue-800": type !== granularity },
                          {
                            "bg-system-blue-600": type === granularity,
                          },
                        )}
                        onClick={() => handleSetDataAggType(type)}
                      >
                        {long}
                      </Button>
                    ))
                  : null}
              </div>
            )}
            {dateRange?.[0] &&
              dateRange?.[1] &&
              isValidDate(dateRange[0]) &&
              isValidDate(dateRange[1]) && (
                <DateRangePicker
                  start={dateRange[0]}
                  end={dateRange[1]}
                  granularity={granularity}
                  compareStart={
                    compareRange?.[0] && isValidDate(compareRange[0])
                      ? compareRange[0]
                      : undefined
                  }
                  lastUpdatedDate={lastDate ? new Date(lastDate) : undefined}
                  comparisonEnabled={true}
                  compareEnd={
                    compareRange?.[1] && isValidDate(compareRange[1])
                      ? compareRange[1]
                      : undefined
                  }
                  onChange={handleChangePeriods}
                />
              )}
            <RichTooltip
              content={
                <LastUpdatedChip
                  usedInTooltip={true}
                  lastDate={lastDate}
                  isLoading={isLastUpdateDateLoading}
                >
                  Max date available in Page:
                </LastUpdatedChip>
              }
            >
              <Card className="p-system-xs relative h-system-2xl w-system-2xl flex items-center justify-center rounded-system-md bg-system-blue-700 border border-system-blue-600 cursor-pointer">
                <span className="absolute -top-system-3xs -right-system-3xs flex size-system-sm">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-system-brand-pink-400 opacity-75"></span>
                  <span className="relative inline-flex size-system-sm rounded-full bg-system-brand-pink-500"></span>
                </span>
                <InformationCircleIcon className="w-system-lg h-system-lg" />
              </Card>
            </RichTooltip>
          </div>
        </div>
        <div className="flex mt-system-md w-full h-200">
          <div className="grow-0 shrink-0 basis-2/3 pe-system-xs">
            <BrandValueOverviewChart
              cubeQueryParams={enrichedCubeQueryParams}
              selectedMetrics={selectedMetrics}
              granularity={granularity}
              dateRange={dateRange}
              compareRange={compareRange ?? undefined}
              graphs={graphsWithColors}
            />
          </div>
          <div className="basis-1/3 grow-0 shrink-0 h-full ps-system-xs">
            <div className="flex flex-col justify-between w-full h-full">
              <div className="w-full h-1/2 pb-system-xs">
                <BrandValueByChannelGroupChart
                  cubeQueryParams={enrichedCubeQueryParams}
                  dateRange={dateRange}
                  compareRange={compareRange ?? undefined}
                />
              </div>
              <div className="w-full h-1/2 pt-system-xs">
                <BrandValueByPayoutCurve
                  cubeQueryParams={enrichedCubeQueryParams}
                  dateRange={dateRange}
                  compareRange={compareRange ?? undefined}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="my-system-lg">
        <TabsGroup
          options={Object.values(tables).map(tab => ({
            label: tab.label,
            id: tab.id,
          }))}
          onChange={value => handleSetWidget(Object.values(tables)[value].id)}
          index={Object.values(tables).findIndex(
            tab => tab.id === activeTable.id,
          )}
        >
          {tab => (
            <div className="flex items-center gap-system-xs">
              <span>{tab.label}</span>
            </div>
          )}
        </TabsGroup>
      </div>
      <div>
        <BrandValueOverviewTable
          cubeQueryParams={enrichedCubeQueryParams}
          widget={activeTable.widget}
          granularity={granularity}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
        />
      </div>
    </div>
  )
}
